import React, { Component, CSSProperties } from 'react'
import compose from 'lodash/flowRight'
import styled from 'styled-components'

import { SIZES } from '~/common/globalStyles'
import {
  SelectComponentWrapper,
  SelectDropdownButton,
  Placeholder,
  Menu,
} from '~/views/components/ui/inputs/v2/Select/shared/components/selectElements'
import { groupArray } from '~/views/components/ui/inputs/v2/shared/utils/selectHelpers'
import {
  ProviderProps,
  withDrawerLockingContext,
} from '~/views/components/WithDrawerLockingContext'

import Downshift from './components/MultiDownshift'

const SelectDropdownInput = styled.input<{ styledInputMode: boolean }>`
  border: none;
  cursor: pointer;
  width: ${({ styledInputMode }) => (styledInputMode ? '100%' : '3px')};

  &:focus {
    outline: 0;
  }
`

interface StyleCss extends CSSProperties {
  padding?: string | number
}

interface WrapperProps {
  focused: boolean
  isOpen: boolean
  styledInputMode: boolean
}

const Wrapper = styled.div<WrapperProps>`
  display: ${({ styledInputMode }) => (styledInputMode ? 'none' : 'flex')};
  flex-wrap: wrap;
  align-items: center;
  opacity: ${({ focused, isOpen }) => (focused && isOpen ? 0.5 : 1)};
`

interface SearchableMultiSelectProps {
  placeholder: string

  renderValue: (selectedItems: string[]) => React.ReactNode
  fullWidth?: boolean
  required?: boolean
  id: string
  disabled?: boolean
  drawerLockingContext: ProviderProps
  filterMethod: (child: any) => any
  grouped: boolean
  groupCriteria: (data: React.ReactElement) => any
  style?: StyleCss
  dataTestId?: string

  onFocus: (event: React.FocusEvent<HTMLInputElement>) => void
  onBlur: (event: React.FocusEvent<HTMLInputElement>) => void
  children: React.ReactNode
}

class SearchableMultiSelect extends Component<SearchableMultiSelectProps> {
  override state = {
    focused: false,
  }
  childrenArray: React.ReactNode[] | null = null
  input: HTMLInputElement | null = null
  searchInput: HTMLInputElement | null = null
  inputMode: boolean | null = null

  override componentDidMount() {
    this.childrenArray = React.Children.toArray(this.props.children)
  }

  override componentDidUpdate(
    prevProps: SearchableMultiSelectProps & { children: React.ReactNode },
  ) {
    if (prevProps.children !== this.props.children) {
      this.childrenArray = React.Children.toArray(this.props.children)
      this.forceUpdate()
    }
  }

  onFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    const { onFocus } = this.props
    this.setState({ focused: true })
    if (onFocus) {
      onFocus(event)
    }
  }

  onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { onBlur } = this.props
    this.setState({ focused: false })
    if (onBlur) {
      onBlur(event)
    }
  }

  onStateChange = (event: any) => {
    const { drawerLockingContext } = this.props
    if (event.isOpen) {
      drawerLockingContext.disableBackdropClick()
    }
  }

  override render() {
    const {
      placeholder,
      renderValue,
      fullWidth,
      required,
      id,
      disabled,
      drawerLockingContext,
      filterMethod,
      grouped,
      groupCriteria,
      dataTestId,
      ...rest
    } = this.props

    return (
      <Downshift
        itemToString={(selected: { name: string } | null) =>
          selected ? selected.name : ''
        }
        onStateChange={this.onStateChange}
        {...rest}
      >
        {({
          getInputProps,
          getItemProps,
          getMenuProps,
          getRootProps,
          isOpen,
          inputValue,
          highlightedIndex,
          selectedItems,
          openMenu,
        }: Record<string, any>) => {
          this.inputMode = !!inputValue

          const { style } = this.props

          return (
            <SelectComponentWrapper {...getRootProps({ refKey: 'innerRef' })}>
              <SelectDropdownButton
                data-testid={`${dataTestId}-select-dropdown`}
                fullWidth={fullWidth}
                disabled={disabled}
                onFocus={this.onFocus}
                onBlur={this.onBlur}
                focused={this.state.focused}
                style={
                  style && style.padding && this.inputMode
                    ? { ...style, padding: `${SIZES.small}px 0px 0px 0px` }
                    : style
                }
                onClick={() => this.searchInput?.focus()}
              >
                <SelectDropdownInput
                  ref={(node) => {
                    this.searchInput = node
                  }}
                  styledInputMode={this.inputMode}
                  {...getInputProps({
                    onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
                      event.target.value = ''
                      openMenu()
                    },
                    disabled,
                  })}
                  style={this.props.style}
                  data-testid={`${dataTestId}-select-input`}
                />
                <Wrapper
                  styledInputMode={this.inputMode}
                  focused={this.state.focused}
                  isOpen={isOpen}
                >
                  {Boolean(placeholder) && selectedItems.length === 0 && (
                    <Placeholder>{placeholder}</Placeholder>
                  )}
                  {typeof renderValue === 'function'
                    ? renderValue(selectedItems as string[])
                    : (selectedItems as Record<string, string>[])
                        .map((item) => item.name)
                        .join(', ')}
                </Wrapper>
                <input
                  id={id}
                  data-testid="downshift-multi-input"
                  required={required}
                  onFocus={this.onFocus}
                  onBlur={this.onBlur}
                  onKeyDown={(event) => {
                    drawerLockingContext &&
                      drawerLockingContext.handleKeyDown(
                        event as unknown as KeyboardEvent,
                      )
                  }}
                  value={selectedItems as string[]}
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                    opacity: 0,
                    pointerEvents: 'none',
                  }}
                  ref={(node) => {
                    this.input = node
                  }}
                />
              </SelectDropdownButton>
              {isOpen && (
                <Menu {...getMenuProps({ open: isOpen })}>
                  {groupArray(
                    getItemProps,
                    inputValue,
                    highlightedIndex,
                    selectedItems,
                    grouped,
                    groupCriteria,
                    filterMethod,
                    this.childrenArray,
                    true,
                  )}
                </Menu>
              )}
            </SelectComponentWrapper>
          )
        }}
      </Downshift>
    )
  }
}

export default compose(withDrawerLockingContext)(SearchableMultiSelect)
